const BASE_URL = process.env.NEXT_PUBLIC_API_URL ?? 'http://localhost:8000';
const BASE_WS_URL = BASE_URL.replace(/^http/, 'ws');

// The tenant is whatever subdomain the browser is actually on right now
// (e.g. acme-corp.yourapp.com -> "acme-corp"), matching the backend's
// schema-per-tenant, subdomain-based resolution — the backend auto-creates a
// tenant + schema the first time it sees a new one, so no separate signup
// step is required here. Returns null when there's no subdomain to read
// (bare "localhost", SSR with no window, a raw IP, etc.) — requests just
// won't carry a tenant header/param in that case, same as visiting the API
// directly with no tenant info.
function currentTenantSubdomain(): string | null {
  if (typeof window === 'undefined') return null;
  const host = window.location.hostname.toLowerCase();
  if (host === 'localhost' || host === '127.0.0.1' || !host.includes('.')) return null;
  return host.split('.')[0];
}

function tenantHeaders(extra?: Record<string, string>): Record<string, string> {
  const subdomain = currentTenantSubdomain();
  return subdomain ? { 'X-Tenant-Subdomain': subdomain, ...extra } : { ...extra };
}

export function getRunWsUrl(runId: string): string {
  // Browsers can't set custom headers on a WebSocket handshake, so the tenant
  // goes as a query param here instead — the backend accepts either.
  const subdomain = currentTenantSubdomain();
  const qs = subdomain ? `?tenant=${encodeURIComponent(subdomain)}` : '';
  return `${BASE_WS_URL}/api/runs/${runId}/ws${qs}`;
}

export interface RunSummary {
  id: string;
  entity: string;
  intent: string;
  data_sources: string[];
  status: 'running' | 'completed' | 'error' | 'interrupted' | 'stopped';
  created_at: string;
  completed_at: string | null;
  entity_type: string | null;
  website: string | null;
  tags: string[];
  social_handles: Record<string, string>;
  overall_sentiment: string | null;
  overall_sentiment_score: number | null;
  total_articles: number | null;
  summary: string | null;
  top_aspects: string[];
  sentiment_positive: number;
  sentiment_negative: number;
  sentiment_neutral: number;
  risk_signals: number;
}

export interface SourceBreakdown {
  source: string;
  total_articles: number;
  positive_count: number;
  negative_count: number;
  neutral_count: number;
  mixed_count: number;
  avg_sentiment_score: number;
  dominant_sentiment: string;
}

export interface Cluster {
  cluster_id: number;
  label: string;
  dominant_sentiment: string;
  weighted_sentiment_score: number;
  avg_intensity: number;
  article_count: number;
  key_findings: string[];
  top_aspects: string[];
  data_sources: string[];
}

export interface RunDetail extends RunSummary {
  source_breakdown: SourceBreakdown[];
  key_themes: string[];
  clusters: Cluster[];
}

export interface CreateRunPayload {
  entity: string;
  context_intent: string;
  data_sources: string[];
  social_handles: Record<string, string>;
  run_mode: string;
  entity_type?: string;
  website?: string;
  tags?: string[];
}

export async function listRuns(params?: {
  status?: string;
  entity?: string;
  limit?: number;
  offset?: number;
}): Promise<RunSummary[]> {
  const qs = new URLSearchParams();
  if (params?.status) qs.set('status', params.status);
  if (params?.entity) qs.set('entity', params.entity);
  if (params?.limit != null) qs.set('limit', String(params.limit));
  if (params?.offset != null) qs.set('offset', String(params.offset));
  const res = await fetch(`${BASE_URL}/api/runs?${qs}`, { headers: tenantHeaders(), cache: 'no-store' });
  if (!res.ok) throw new Error(`listRuns: ${res.status}`);
  return res.json();
}

export async function createRun(payload: CreateRunPayload): Promise<{ run_id: string }> {
  const res = await fetch(`${BASE_URL}/api/runs`, {
    method: 'POST',
    headers: tenantHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as Record<string, unknown>;
    throw new Error((err.detail as string) ?? `createRun: ${res.status}`);
  }
  return res.json();
}

export async function getRun(runId: string): Promise<RunDetail> {
  const res = await fetch(`${BASE_URL}/api/runs/${runId}`, { headers: tenantHeaders(), cache: 'no-store' });
  if (!res.ok) throw new Error(`getRun: ${res.status}`);
  return res.json();
}

export async function getRunResults(runId: string): Promise<{
  run: RunDetail;
  articles: unknown[];
  clusters: unknown[];
  overview: unknown;
  search_results: unknown[];
}> {
  const res = await fetch(`${BASE_URL}/api/runs/${runId}/results`, { headers: tenantHeaders(), cache: 'no-store' });
  if (!res.ok) throw new Error(`getRunResults: ${res.status}`);
  return res.json();
}

export interface Article {
  id: number;
  run_id: string;
  url: string;
  title: string | null;
  source: string | null;
  data_source: string;
  status: string;
  relevance_score: number | null;
  relevance_reason: string | null;
  needs_review: boolean;
  aspects: string[] | null;
  sentiment: string | null;
  sentiment_intensity: number | null;
  sentiment_rationale: string | null;
  content_snippet: string | null;
  published_at: string | null;
  created_at: string;
}

export interface SearchResult {
  id: number;
  run_id: string;
  url: string;
  title: string | null;
  snippet: string | null;
  score: number | null;
  query: string | null;
  topic: string | null;
  data_source: string;
  published_at: string | null;
  created_at: string;
  // Enriched via LEFT JOIN with articles (available after analysis completes)
  sentiment: string | null;
  sentiment_intensity: number | null;
  aspects: string[] | null;
}

export async function getRunArticles(
  runId: string,
  params?: { data_source?: string; sentiment?: string; limit?: number; offset?: number },
): Promise<Article[]> {
  const qs = new URLSearchParams();
  if (params?.data_source) qs.set('data_source', params.data_source);
  if (params?.sentiment) qs.set('sentiment', params.sentiment);
  if (params?.limit != null) qs.set('limit', String(params.limit));
  if (params?.offset != null) qs.set('offset', String(params.offset));
  const res = await fetch(`${BASE_URL}/api/runs/${runId}/articles?${qs}`, { headers: tenantHeaders(), cache: 'no-store' });
  if (!res.ok) throw new Error(`getRunArticles: ${res.status}`);
  return res.json();
}

export interface AppendSourcePayload {
  data_sources: string[];
  social_handles?: Record<string, string>;
  from_date?: string;
  to_date?: string;
}

export async function appendSource(
  runId: string,
  payload: AppendSourcePayload,
): Promise<{ status: string; run_id: string; agentic_run_id: string }> {
  const res = await fetch(`${BASE_URL}/api/runs/${runId}/append`, {
    method: 'POST',
    headers: tenantHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(payload),
    cache: 'no-store',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as Record<string, unknown>;
    throw new Error((err.detail as string) ?? `appendSource: ${res.status}`);
  }
  return res.json();
}

export async function deactivateRun(runId: string): Promise<{ status: string; run_id: string }> {
  const res = await fetch(`${BASE_URL}/api/runs/${runId}`, {
    method: 'DELETE',
    headers: tenantHeaders(),
    cache: 'no-store',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as Record<string, unknown>;
    throw new Error((err.detail as string) ?? `deactivateRun: ${res.status}`);
  }
  return res.json();
}

export async function reanalyzeRun(runId: string): Promise<{ status: string; run_id: string }> {
  const res = await fetch(`${BASE_URL}/api/runs/${runId}/reanalyze`, {
    method: 'POST',
    headers: tenantHeaders(),
    cache: 'no-store',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as Record<string, unknown>;
    throw new Error((err.detail as string) ?? `reanalyzeRun: ${res.status}`);
  }
  return res.json();
}

export async function stopRun(runId: string): Promise<{ status: string; run_id: string }> {
  const res = await fetch(`${BASE_URL}/api/runs/${runId}/stop`, {
    method: 'POST',
    headers: tenantHeaders(),
    cache: 'no-store',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as Record<string, unknown>;
    throw new Error((err.detail as string) ?? `stopRun: ${res.status}`);
  }
  return res.json();
}

export async function getRunSearchResults(
  runId: string,
  params?: { data_source?: string; limit?: number; offset?: number },
): Promise<SearchResult[]> {
  const qs = new URLSearchParams();
  if (params?.data_source) qs.set('data_source', params.data_source);
  if (params?.limit != null) qs.set('limit', String(params.limit));
  if (params?.offset != null) qs.set('offset', String(params.offset));
  const res = await fetch(`${BASE_URL}/api/runs/${runId}/search-results?${qs}`, { headers: tenantHeaders(), cache: 'no-store' });
  if (!res.ok) throw new Error(`getRunSearchResults: ${res.status}`);
  return res.json();
}
